import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import {
  CurrencyValue,
  Flex,
  BarChart,
  DisplaySwitch,
  formatDatetime,
  formatCurrency
} from '@blockanalitica/ui';
import { useMemo, useState, useCallback } from 'react';
import { useTheme } from 'styled-components';

export default function HistoricTabs() {
  const theme = useTheme();
  const [activeDisplayOption, setActiveDisplayOption] = useState('sky_buyback');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const displayOptions = useMemo(
    () => [
      { key: 'sky_buyback', value: 'SKY Buyback Volume' },
      { key: 'suds_spend', value: 'USDS Spend Volume' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/buyback/historic-volume/',
    queryParams: { days_ago: chartDaysAgo }
  });

  if (error) throw error;

  const timeFilter = (
    <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
  );

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'sky_buyback':
        return [
          {
            xField: 'date',
            yField: 'sky_buyback',
            label: 'Sky Buybacks',
            backgroundColor: theme.colors.chartUp
          }
        ];
      case 'suds_spend':
        return [
          {
            xField: 'date',
            yField: 'usds_spend',
            label: 'USDS Spend',
            backgroundColor: theme.colors.chartDown
          }
        ];
      default:
        return [];
    }
  }, [activeDisplayOption, theme.colors]);

  const yScaleValueFormatter = (value) => formatCurrency(value);

  const valueFormatter = (value) => (
    <CurrencyValue
      value={value}
      options={{
        currency: 'USD'
      }}
    />
  );

  const subValueFormatter = useCallback(
    (value) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        <BarChart
          key={activeDisplayOption}
          data={data}
          datasets={datasets}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          subValueFormatter={subValueFormatter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
